import 'mapbox-gl/dist/mapbox-gl.css'

import type { Feature, FeatureCollection } from 'geojson'
import { useLocality } from 'location/LocalityProvider'
import type {
  AnySourceImpl,
  CircleLayer,
  GeoJSONSource,
  LngLatLike,
  MapboxGeoJSONFeature,
  SymbolLayer,
} from 'mapbox-gl'
import mapboxgl from 'mapbox-gl'
import { MAPBOX_STYLE, MAPBOX_TOKEN } from 'maps/mapboxConfig'
import type { FC, MutableRefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import type { ObjectsMapType } from '../../Map.model'
import { clusterCircle, clusterCount } from './layersSettings'
import { MapboxContainer } from './Mapbox.styled'

mapboxgl.accessToken = MAPBOX_TOKEN

interface IMapboxProps {
  featureCollection: FeatureCollection
  callbackData: (data: ObjectsMapType[]) => void
}

const sourceID = 'cluster_data'
const clusterLayerName = 'clusters'

export const Mapbox: FC<IMapboxProps> = ({
  featureCollection,
  callbackData,
}) => {
  const { locality } = useLocality()
  const userCoords = locality
  const mapContainer = useRef(null) as MutableRefObject<any>
  const map = useRef(null) as MutableRefObject<any>
  const center = useMemo(
    () =>
      userCoords?.lon && userCoords?.lat
        ? [userCoords.lon, userCoords.lat]
        : [39.718631, 47.223049],
    [userCoords],
  ) as LngLatLike

  const featureCollectionOrEmptyData = useMemo(() => {
    if (featureCollection?.features?.length) {
      return featureCollection
    }
    return {
      type: 'FeatureCollection',
      features: [],
    }
  }, [featureCollection])

  const handleSetSelectedObj = useCallback(
    (data: ObjectsMapType[]) => {
      callbackData(data)
    },
    [callbackData],
  )

  useEffect(() => {
    if (map.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAPBOX_STYLE,
      center,
      zoom: 12,
      maxZoom: 18,
      minZoom: 7,
    })
    map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
  }, [center])

  useEffect(
    () => () => {
      map.current?.remove()
      map.current = null
    },
    [],
  )

  useEffect(() => {
    if (map.current && userCoords?.lon && userCoords.lat) {
      map.current.setCenter([userCoords.lon, userCoords.lat] as LngLatLike)
    }
  }, [userCoords])

  const onClickCluster = useCallback(
    (e: any) => {
      const featuresFromCluster: MapboxGeoJSONFeature[] =
        map.current.queryRenderedFeatures(e.point, {
          layers: [clusterLayerName],
        })

      if (featuresFromCluster && featuresFromCluster.length) {
        const { properties }: any = featuresFromCluster[0]

        if (properties && 'point_count' in properties) {
          const { cluster_id: clusterId, point_count: pointCount } = properties

          const clusterSource: AnySourceImpl = map.current.getSource(
            sourceID,
          ) as unknown as GeoJSONSource

          if (clusterSource) {
            clusterSource.getClusterLeaves(
              clusterId,
              pointCount,
              0,
              (error, features: Feature[]) => {
                if (!error) {
                  const a = features.reduce((acc, iter) => {
                    // @ts-ignore
                    acc.push(iter.properties)
                    return acc
                  }, [] as unknown as ObjectsMapType[])
                  handleSetSelectedObj(a)
                }
                return null
              },
            )
          }
        } else {
          handleSetSelectedObj([properties])
        }
      }
    },
    [handleSetSelectedObj],
  )

  useEffect(() => {
    if (!map.current) return undefined
    const load = () => {
      if (!map.current.getSource(sourceID)) {
        map.current.addSource(sourceID, {
          type: 'geojson',
          data: featureCollectionOrEmptyData,
          cluster: true,
          clusterMaxZoom: 25,
          clusterRadius: 10,
        })
      }
      if (!map.current.getLayer('clusters')) {
        map.current.addLayer({
          ...clusterCircle,
          source: sourceID,
        } as CircleLayer)
      }
      if (!map.current.getLayer('cluster-count')) {
        map.current.addLayer({
          ...clusterCount,
          source: sourceID,
        } as SymbolLayer)
      }
    }
    if (map.current.isStyleLoaded()) load()
    else map.current.on('load', load)
    return () => map.current?.off('load', load)
  }, [featureCollectionOrEmptyData])

  useEffect(() => {
    map.current.off('click', clusterLayerName, onClickCluster)
    map.current.on('click', clusterLayerName, onClickCluster)
    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer'
    })
    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = ''
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!map.current) return

    const isSourceFeaturesLengthValid = map.current.getSource(sourceID)

    if (isSourceFeaturesLengthValid) {
      map.current.getSource(sourceID).setData(featureCollectionOrEmptyData)
    }
  }, [featureCollectionOrEmptyData])

  useEffect(() => {
    if (!map.current || !locality || (locality.lat && locality.lon)) return
    const bounds = new mapboxgl.LngLatBounds()
    featureCollection?.features?.forEach((feature) => {
      if (feature.geometry.type === 'Point') {
        const [lon, lat] = feature.geometry.coordinates
        bounds.extend([lon, lat])
      }
    })
    if (!bounds.isEmpty())
      map.current.fitBounds(bounds, { padding: 80, maxZoom: 12 })
  }, [featureCollection, locality])

  return <MapboxContainer ref={mapContainer} />
}
